import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, User, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";
import axios from "axios";
import MatrixRain from "./MatrixRain";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Field = ({ icon: Icon, ...props }) => (
  <label className="flex items-center gap-3 border border-primary/30 bg-black/60 px-4 py-3 focus-within:border-primary transition-colors">
    <Icon className="w-4 h-4 text-primary/70 shrink-0" />
    <input
      {...props}
      className="w-full bg-transparent outline-none font-mono text-sm text-primary placeholder:text-primary/30"
    />
  </label>
);

const Login = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(() => navigate("/dashboard", { replace: true }))
      .catch(() => {});
    axios.get(`${API}/auth/status`)
      .then((r) => {
        setStatus(r.data);
        if (r.data.needs_setup) setMode("setup");
      })
      .catch(() => setError("Cannot reach the Alpha1 server."));
  }, [navigate]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = mode === "login" ? "login" : "register";
      const body = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const r = await axios.post(`${API}/auth/${path}`, body, { withCredentials: true });
      navigate("/dashboard", { replace: true, state: { user: r.data.user } });
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const isSetup = mode === "setup";
  const title = isSetup ? "CREATE OWNER ACCOUNT" : mode === "register" ? "REQUEST ACCESS" : "OPERATOR LOGIN";

  return (
    <div className="min-h-screen bg-black text-primary flex items-center justify-center px-4 relative overflow-hidden">
      <MatrixRain />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md cyber-card p-8 bg-black/80"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono text-primary/50 hover:text-primary mb-6">
          <ArrowLeft className="w-3 h-3" /> BACK
        </Link>
        <div className="text-center mb-8">
          <h1 className="font-military text-3xl tracking-wider text-glow-primary">ALPHA1-ZERO</h1>
          <p className="text-[10px] font-mono tracking-[0.3em] text-primary/50 mt-1">SKIDRIVE TECHNOLOGIES</p>
          <p className="font-mono text-sm mt-6 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" /> {title}
          </p>
          {isSetup && (
            <p className="text-xs font-mono text-primary/50 mt-2">
              First login on this server. This account becomes the owner.
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode !== "login" && (
            <Field icon={User} type="text" placeholder="Name" value={form.name} onChange={update("name")} required autoComplete="name" />
          )}
          <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={update("email")} required autoComplete="email" />
          <Field
            icon={Lock}
            type="password"
            placeholder={mode === "login" ? "Password" : "Password (10+ characters)"}
            value={form.password}
            onChange={update("password")}
            required
            minLength={mode === "login" ? undefined : 10}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
          />

          {error && (
            <p className="flex items-center gap-2 text-xs font-mono text-red-400">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </p>
          )}

          <button type="submit" disabled={busy || !status} className="cyber-btn w-full text-sm disabled:opacity-50">
            {busy ? "AUTHENTICATING..." : isSetup ? "CREATE ACCOUNT" : mode === "register" ? "SIGN UP" : "ACCESS SYSTEM"}
          </button>
        </form>

        {!isSetup && status?.signup_open && (
          <button
            type="button"
            onClick={() => { setError(""); setMode(mode === "login" ? "register" : "login"); }}
            className="w-full mt-4 text-xs font-mono text-primary/50 hover:text-primary"
          >
            {mode === "login" ? "No account? Sign up" : "Have an account? Log in"}
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default Login;
